import type {
  Achievement,
  Collection,
  CrowdGuessAggregate,
  DailyMeta,
  DuelSummary,
  EraFilter,
  EventSummary,
  HintReveal,
  HintType,
  LeaderboardEntry,
  LeaderboardRegion,
  LeagueDetail,
  LeagueSummary,
  PersonalStats,
  MissionSummary,
  PublicProfile,
  ReplaySession,
  RevealPayload,
  RoundScoreboard,
  SeasonStanding,
  SessionMeta,
  SessionMode,
  TheatrePlayEvent,
  TheatreProgress,
  TheatreTourFull,
  TheatreTourSummary,
  TickerEntry,
  WeeklyRecap
} from "../types";

const TOKEN_STORAGE_KEY = "chronoscape.player-token.v1";
const NAME_STORAGE_KEY = "chronoscape.player-name.v1";

type PlayerHandle = {
  id: string;
  token: string;
  name: string;
};

class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
  }
}

let player: PlayerHandle | null = null;
let bootstrapping: Promise<PlayerHandle> | null = null;

async function bootstrapPlayer(): Promise<PlayerHandle> {
  if (player) {
    return player;
  }

  const existingToken = window.localStorage.getItem(TOKEN_STORAGE_KEY);
  if (existingToken) {
    const response = await fetch("/api/players/me", {
      headers: { Authorization: `Bearer ${existingToken}` }
    });

    if (response.ok) {
      const body = await response.json();
      player = { id: body.id, token: existingToken, name: body.name };
      return player;
    }

    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  }

  const seedName = window.localStorage.getItem(NAME_STORAGE_KEY) ?? "";
  const response = await fetch("/api/players", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name: seedName })
  });

  if (!response.ok) {
    throw new ApiError(response.status, "Could not register player");
  }

  const body = (await response.json()) as PlayerHandle;
  window.localStorage.setItem(TOKEN_STORAGE_KEY, body.token);
  player = body;
  return body;
}

export async function ensurePlayer(): Promise<PlayerHandle> {
  if (!bootstrapping) {
    bootstrapping = bootstrapPlayer();
  }

  return bootstrapping;
}

async function authedFetch(input: string, init?: RequestInit): Promise<Response> {
  const handle = await ensurePlayer();
  const headers = new Headers(init?.headers);
  headers.set("Authorization", `Bearer ${handle.token}`);
  if (init?.body) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(input, { ...init, headers });
  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  return response;
}

export async function setDisplayName(name: string): Promise<string> {
  const handle = await ensurePlayer();
  const response = await authedFetch("/api/players/me", {
    method: "PATCH",
    body: JSON.stringify({ name })
  });
  const body = await response.json();
  player = { ...handle, name: body.name };
  window.localStorage.setItem(NAME_STORAGE_KEY, body.name);
  return body.name;
}

export async function getDisplayName(): Promise<string> {
  const handle = await ensurePlayer();
  return handle.name;
}

export async function startSession(args: {
  mode: SessionMode;
  filter: EraFilter;
  duelCode?: string;
  eventSlug?: string;
  roundPool?: "beginner";
  excludeRoundIds?: string[];
}): Promise<SessionMeta> {
  const response = await authedFetch("/api/sessions", {
    method: "POST",
    body: JSON.stringify(args)
  });
  return (await response.json()) as SessionMeta;
}

export async function continueSession(sessionId: string): Promise<SessionMeta> {
  const response = await authedFetch(`/api/sessions/${sessionId}/continue`, {
    method: "POST",
    body: JSON.stringify({})
  });
  return (await response.json()) as SessionMeta;
}

export async function fetchCollection(): Promise<Collection> {
  const response = await authedFetch("/api/collection");
  return (await response.json()) as Collection;
}

export async function fetchEvents(): Promise<EventSummary[]> {
  const response = await authedFetch("/api/events");
  const body = await response.json();
  return body.events as EventSummary[];
}

export async function fetchEventLeaderboard(slug: string): Promise<{
  event: EventSummary;
  entries: Array<{ score: number; name: string; username: string | null; createdAt: string }>;
}> {
  const response = await authedFetch(`/api/events/${slug}/leaderboard`);
  return await response.json();
}

export async function createLeague(name: string): Promise<LeagueSummary> {
  const response = await authedFetch("/api/leagues", {
    method: "POST",
    body: JSON.stringify({ name })
  });
  return (await response.json()) as LeagueSummary;
}

export async function joinLeague(code: string): Promise<LeagueSummary> {
  const response = await authedFetch(`/api/leagues/${encodeURIComponent(code)}/join`, {
    method: "POST",
    body: JSON.stringify({})
  });
  return (await response.json()) as LeagueSummary;
}

export async function leaveLeague(code: string): Promise<void> {
  await authedFetch(`/api/leagues/${encodeURIComponent(code)}/leave`, {
    method: "POST",
    body: JSON.stringify({})
  });
}

export async function fetchMyLeagues(): Promise<LeagueSummary[]> {
  const response = await authedFetch("/api/leagues/me");
  const body = await response.json();
  return body.leagues as LeagueSummary[];
}

export async function fetchLeagueDetail(code: string): Promise<LeagueDetail> {
  const response = await authedFetch(`/api/leagues/${encodeURIComponent(code)}`);
  return (await response.json()) as LeagueDetail;
}

export async function fetchUsername(): Promise<string | null> {
  const response = await authedFetch("/api/profile/me/username");
  const body = await response.json();
  return body.username ?? null;
}

export async function claimUsername(username: string): Promise<string> {
  const response = await authedFetch("/api/profile/me/username", {
    method: "POST",
    body: JSON.stringify({ username })
  });
  const body = await response.json();
  return body.username as string;
}

export async function fetchProfile(username: string): Promise<PublicProfile> {
  const response = await fetch(`/api/profile/${encodeURIComponent(username)}`);
  if (!response.ok) {
    throw new ApiError(response.status, await response.text());
  }
  return (await response.json()) as PublicProfile;
}

export type IronModeFlags = {
  mapLabelsHidden: boolean;
  eraStampHidden: boolean;
  hintsDisabled: boolean;
};

export async function revealRound(args: {
  sessionId: string;
  roundId: string;
  guess: { lat: number | null; lng: number | null; year: number };
  wager: number;
  elapsedMs: number;
  ironMode?: IronModeFlags;
  detectivePhase?: 1 | 2 | 3 | 4;
}): Promise<RevealPayload> {
  const guess = {
    lat: args.guess.lat,
    lng: args.guess.lng,
    year: args.guess.year
  };
  const response = await authedFetch(`/api/sessions/${args.sessionId}/reveal`, {
    method: "POST",
    body: JSON.stringify({
      roundId: args.roundId,
      guess,
      wager: args.wager,
      elapsedMs: args.elapsedMs,
      ironMode: args.ironMode,
      detectivePhase: args.detectivePhase
    })
  });
  return (await response.json()) as RevealPayload;
}

export async function takeHint(args: {
  sessionId: string;
  roundId: string;
  type: HintType;
}): Promise<{ reveal: HintReveal; totalHintCost: number }> {
  const response = await authedFetch(`/api/sessions/${args.sessionId}/hint`, {
    method: "POST",
    body: JSON.stringify({ roundId: args.roundId, type: args.type })
  });
  return await response.json();
}

export async function fetchCrowdGuesses(roundId: string): Promise<CrowdGuessAggregate> {
  const response = await authedFetch(`/api/rounds/${roundId}/guesses`);
  return (await response.json()) as CrowdGuessAggregate;
}

export async function fetchReplay(sessionId: string): Promise<ReplaySession> {
  const response = await authedFetch(`/api/spectate/${sessionId}`);
  return (await response.json()) as ReplaySession;
}

export async function fetchPersonalStats(): Promise<PersonalStats> {
  const response = await authedFetch("/api/stats/me");
  return (await response.json()) as PersonalStats;
}

export type GlobalHomepageStats = {
  sessionsToday: number;
  bestScoreToday: number | null;
  totalPlayers: number;
  liveNow: number;
  totalScenes: number;
};

export async function fetchGlobalStats(): Promise<GlobalHomepageStats> {
  const response = await fetch("/api/stats/global");
  if (!response.ok) throw new Error(`stats/global ${response.status}`);
  return (await response.json()) as GlobalHomepageStats;
}

export type AtlasProgressEntry = {
  bestScore: number;
  bestDistanceKm: number;
  bestYearsOff: number;
  plays: number;
};

export type AtlasProgress = {
  totalScenes: number;
  played: Record<string, AtlasProgressEntry>;
};

export async function fetchAtlasProgress(): Promise<AtlasProgress> {
  const response = await authedFetch("/api/stats/atlas/me");
  return (await response.json()) as AtlasProgress;
}

export async function fetchWeeklyRecap(): Promise<WeeklyRecap> {
  const response = await authedFetch("/api/recap/week");
  return (await response.json()) as WeeklyRecap;
}

export async function fetchSeasonStanding(): Promise<SeasonStanding> {
  const response = await authedFetch("/api/seasons/me");
  return (await response.json()) as SeasonStanding;
}

export async function fetchEmailLeaderboard(limit = 10): Promise<{
  top: { id: string; name: string; score: number; createdAt: string } | null;
  entries: Array<{ id: string; name: string; score: number; filter: string; mode: string; createdAt: string }>;
}> {
  const response = await fetch(`/api/email-leaderboard?limit=${limit}`);
  if (!response.ok) {
    return { top: null, entries: [] };
  }
  return await response.json();
}

export async function fetchMissions(): Promise<MissionSummary[]> {
  const response = await authedFetch("/api/missions");
  const body = await response.json();
  return body.missions as MissionSummary[];
}

export async function startMission(slug: string): Promise<SessionMeta> {
  const response = await authedFetch(`/api/missions/${encodeURIComponent(slug)}/start`, {
    method: "POST",
    body: JSON.stringify({})
  });
  return (await response.json()) as SessionMeta;
}

export async function fetchRoundScoreboard(): Promise<RoundScoreboard> {
  try {
    const response = await fetch("/api/rounds/scoreboard");
    if (!response.ok) return {};
    const body = (await response.json()) as { scoreboard: RoundScoreboard };
    return body.scoreboard ?? {};
  } catch {
    return {};
  }
}

export async function fetchTickerEntries(): Promise<TickerEntry[]> {
  try {
    const response = await fetch("/api/ticker");
    if (!response.ok) return [];
    const body = (await response.json()) as { entries: TickerEntry[] };
    return body.entries ?? [];
  } catch {
    return [];
  }
}

export async function submitEmailScore(args: {
  sessionId: string;
  email: string;
  displayName: string;
  consent: boolean;
}): Promise<{ id: string; name: string; score: number }> {
  const response = await authedFetch("/api/email-leaderboard/submit", {
    method: "POST",
    body: JSON.stringify(args)
  });
  return await response.json();
}

export async function subscribeToPush(subscription: PushSubscription): Promise<void> {
  await authedFetch("/api/push/subscribe", {
    method: "POST",
    body: JSON.stringify({ subscription: subscription.toJSON() })
  });
}

export async function fetchPushPublicKey(): Promise<string | null> {
  try {
    const response = await fetch("/api/push/public-key");
    if (!response.ok) return null;
    const body = await response.json();
    return body.configured ? (body.publicKey as string) : null;
  } catch {
    return null;
  }
}

export async function getSession(sessionId: string): Promise<SessionMeta> {
  const response = await authedFetch(`/api/sessions/${sessionId}`);
  return (await response.json()) as SessionMeta;
}

export async function getDailyStatus(): Promise<DailyMeta> {
  const response = await authedFetch("/api/daily");
  return (await response.json()) as DailyMeta;
}

export async function getLeaderboard(args: {
  filter: EraFilter;
  region: LeaderboardRegion;
  mode?: SessionMode;
  limit?: number;
}): Promise<LeaderboardEntry[]> {
  const params = new URLSearchParams({
    filter: args.filter,
    region: args.region,
    limit: String(args.limit ?? 25)
  });
  if (args.mode) {
    params.set("mode", args.mode);
  }

  const response = await authedFetch(`/api/leaderboard?${params.toString()}`);
  const body = await response.json();
  return body.entries as LeaderboardEntry[];
}

export async function listAchievements(): Promise<Array<Achievement & { unlockedAt: string | null }>> {
  const response = await authedFetch("/api/achievements");
  const body = await response.json();
  return body.achievements;
}

export async function createDuel(sessionId: string): Promise<DuelSummary> {
  const response = await authedFetch("/api/duels", {
    method: "POST",
    body: JSON.stringify({ sessionId })
  });
  return (await response.json()) as DuelSummary;
}

export async function getDuel(code: string): Promise<DuelSummary> {
  const response = await authedFetch(`/api/duels/${code}`);
  return (await response.json()) as DuelSummary;
}

export type DuelOpponentRound = {
  roundId: string;
  score: number;
  guessLat: number | null;
  guessLng: number | null;
  guessYear: number;
};

export async function compareDuel(code: string, sessionId: string): Promise<{
  opponent: { name: string; total: number; perRound: DuelOpponentRound[] };
  you: { perRound: Array<{ roundId: string; score: number }> };
}> {
  const response = await authedFetch(`/api/duels/${code}/compare/${sessionId}`);
  return await response.json();
}

export function buildDuelShareUrl(code: string): string {
  const base = `${window.location.origin}${window.location.pathname}`.replace(/\/$/, "");
  return `${base}?duel=${encodeURIComponent(code)}`;
}

export function readDuelCodeFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const value = params.get("duel");
  return value ? value.trim().toUpperCase() : null;
}

export function clearDuelCodeFromUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete("duel");
  window.history.replaceState({}, "", url.toString());
}

export async function listTheatreTours(): Promise<TheatreTourSummary[]> {
  const response = await authedFetch("/api/theatre/tours");
  const body = await response.json();
  return body.tours as TheatreTourSummary[];
}

export async function getTheatreTour(tourId: string): Promise<TheatreTourFull> {
  const response = await authedFetch(`/api/theatre/tours/${encodeURIComponent(tourId)}`);
  return (await response.json()) as TheatreTourFull;
}

export async function getTheatreProgress(tourId: string): Promise<TheatreProgress | null> {
  const response = await authedFetch(`/api/theatre/progress/${encodeURIComponent(tourId)}`);
  const body = await response.json();
  return (body.progress ?? null) as TheatreProgress | null;
}

export async function postTheatreProgress(args: {
  tourId: string;
  segmentIndex: number;
  positionSec: number;
  completedSegments: string[];
}): Promise<void> {
  await authedFetch("/api/theatre/progress", {
    method: "POST",
    body: JSON.stringify(args)
  });
}

export async function postTheatreEvent(event: TheatrePlayEvent): Promise<void> {
  await authedFetch("/api/theatre/event", {
    method: "POST",
    body: JSON.stringify(event)
  });
}
